import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Trash2, RefreshCw, Wand2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

// Super-admin Project Management tool: bulk archive / delete (to Trash) leftover projects,
// with a one-click selector for development entries (TEST / QA / FUSION). Navy/gold, zero-gap.
export function AdminProjects() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/projects"); setRows(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = rows.filter((r) => showArchived || !r.archived);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const devCount = visible.filter((r) => r.dev_match && !r.archived).length;
  const selectDev = () => setSel(visible.filter((r) => r.dev_match && !r.archived).map((r) => r.id));
  const allChecked = visible.length > 0 && sel.length === visible.length;

  const run = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/projects/bulk", { ids: sel, action });
      toast.success(`${data.count} project(s) ${action === "delete" ? "moved to Trash" : action === "archive" ? "archived" : "unarchived"}`);
      setSel([]); setConfirm(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="shadow-none overflow-hidden" data-testid="admin-projects-card" style={{ borderColor: "#1e2a44" }}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" style={{ background: "#0B1420" }}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold" style={{ color: "#FFFFFF" }}>Project Management</span>
          <Badge variant="outline" className="border-white/20 text-slate-200">{visible.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="h-8 text-slate-200 hover:bg-white/10" onClick={() => setShowArchived((v) => !v)} data-testid="admin-projects-toggle-archived">
            {showArchived ? "Hide archived" : "Show archived"}
          </Button>
          <Button size="sm" className="h-8" style={{ background: "#C20511", color: "#FFFFFF" }} onClick={selectDev} disabled={!devCount} data-testid="admin-projects-select-dev">
            <Wand2 size={13} className="mr-1.5" /> Select dev ({devCount})
          </Button>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-200 hover:bg-white/10" onClick={load} title="Refresh" data-testid="admin-projects-refresh"><RefreshCw size={14} /></Button>
        </div>
      </div>

      {sel.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b" style={{ background: "#FDECEC", borderColor: "#F6CBCB" }} data-testid="admin-projects-bulkbar">
          <span className="text-xs font-semibold" style={{ color: "#92400E" }}>{sel.length} selected</span>
          <Button size="sm" className="h-8" style={{ background: "#0B1420", color: "#FFFFFF" }} onClick={() => setConfirm("archive")} data-testid="admin-projects-archive"><Archive size={13} className="mr-1.5" /> Archive</Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => run("unarchive")} disabled={busy} data-testid="admin-projects-unarchive"><ArchiveRestore size={13} className="mr-1.5" /> Unarchive</Button>
          <Button size="sm" variant="destructive" className="h-8" onClick={() => setConfirm("delete")} data-testid="admin-projects-delete"><Trash2 size={13} className="mr-1.5" /> Delete</Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setSel([])} data-testid="admin-projects-clear">Clear</Button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="border-b border-border text-left">
            <th className="px-4 py-2.5 w-8">
              <input type="checkbox" data-testid="admin-projects-select-all" checked={allChecked}
                onChange={(e) => setSel(e.target.checked ? visible.map((r) => r.id) : [])} className="h-4 w-4 accent-[#C20511]" />
            </th>
            {["Project", "Mode", "Created by", "Areas", "Status", "Updated"].map((h) => <th key={h} className="px-4 py-2.5 overline">{h}</th>)}
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground" data-testid="admin-projects-empty">No projects.</td></tr>
            ) : visible.map((r) => (
              <tr key={r.id} className="border-b border-border/60" data-testid={`admin-project-row-${r.id}`}>
                <td className="px-4 py-2.5">
                  <input type="checkbox" data-testid={`admin-project-check-${r.id}`} checked={sel.includes(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4 accent-[#C20511]" />
                </td>
                <td className="px-4 py-2.5 font-medium">
                  {r.name}
                  {r.dev_match && <Badge className="ml-2 text-[10px] bg-[#C20511] text-white hover:bg-[#C20511]" data-testid={`admin-project-devbadge-${r.id}`}>DEV</Badge>}
                  {r.archived && <Badge variant="outline" className="ml-2 text-[10px]">Archived</Badge>}
                </td>
                <td className="px-4 py-2.5 text-xs">{r.mode || "remote_sensing"}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.created_by || "-"}</td>
                <td className="px-4 py-2.5 tabular-nums">{r.area_count}</td>
                <td className="px-4 py-2.5 text-xs">{r.status || "-"}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="admin-projects-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "delete" ? "Move to Trash?" : "Archive projects?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "delete"
                ? `${sel.length} project(s) will be moved to Trash (auto-purged after 24h) and hidden from the production site.`
                : `${sel.length} project(s) will be archived and hidden from the main project lists. Restore them any time with "Show archived".`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => run(confirm)} disabled={busy} data-testid="admin-projects-confirm-btn">
              {confirm === "delete" ? "Delete" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
